import React from 'react';
import { useTranslation } from 'react-i18next';
import { LANGUAGES } from '../../../../../../components/translations/translationModal';
import { FrameCard, Form, TranslateDropdown } from '@weezevent/nacre';
import BackgroundInput from './backgroundInput';
import ColorInput from './colorInput';
import CounterInput from './counterInput';
import ImageInput from './imageInput';
import TextInput from './textInput';
import SelectInput from './selectInput';
import TextAreaInput from './textAreaInput';
import ToggleInput from './toggleInput';
import SelectField from './selectField';
import Style from './formComponents.module.css';

const FIELD = {
    number: CounterInput,
    text: TextAreaInput,
    string: TextInput,
    boolean: ToggleInput,
    background: BackgroundInput,
    image: ImageInput,
    color: ColorInput,
    select: SelectInput,
    select_field: SelectField
};

export function snakeToCamelCaseObject(snakeObject) {
    const camelObject = {};
    const camelizeString = str => {
        const strArray = str.split('_').map((word, index) => {
            if (index === 0 || !word.length) return word;
            return word[0].toUpperCase() + word.substring(1);
        });
        return strArray.join('');
    };
    Object.entries(snakeObject).forEach(([key, value]) => {
        camelObject[camelizeString(key)] = value;
    });
    return camelObject;
}

export function FormComponent({ type, label = '', description = '', labelOptions = [], defaultValue, options = [], onChange = () => {}, params = {} }) {
    const { t } = useTranslation();

    const selectOptions = React.useMemo(() => {
        if (!options.length) return [];
        return options?.map((option, index) => {
            return {
                text: labelOptions.length >= index ? labelOptions[index] : index,
                key: option,
                value: option
            };
        });
    }, [options, labelOptions]);

    const inlineLabel = React.useMemo(() => {
        return (
            <div>
                <Form.Label optionalText={params.optional ? t('ticket.common.labelOptional') : null}>{label}</Form.Label>
                {/* textInput and toggleInput have a props for description so we'll use it */}
                {!['string', 'boolean'].includes(type) && description && <Form.Sublabel>{description}</Form.Sublabel>}
            </div>
        );
    }, [type, label, description, params]);

    if (!Object.keys(FIELD).includes(type)) return null;
    const Component = FIELD[type];
    const camelParams = snakeToCamelCaseObject(params);
    if (type === 'select_field')
        return <Component label={inlineLabel} description={description} value={defaultValue} options={selectOptions} onChange={onChange} params={camelParams} />;
    return <Component label={inlineLabel} description={description} value={defaultValue} options={selectOptions} onChange={onChange} {...camelParams} />;
}

export function FormComponents({ languages = null, selectedLanguages = null, labels, descriptions, fields, config, setConfig }) {
    const { t } = useTranslation();
    const [currentLanguage, setCurrentLanguage] = React.useState('default');

    const updateConfig = React.useCallback(
        (key, newValue) => {
            let newConfig = config;
            if (!Object.keys(newConfig).includes(currentLanguage)) newConfig[currentLanguage] = {};
            const keyExists = Object.keys(newConfig[currentLanguage]).includes(key);
            const isDefault = Object.keys(newConfig['default']).includes(key) && newValue === newConfig['default'][key];
            //remove fields equal to default and remove empty languages
            if (keyExists && isDefault && currentLanguage !== 'default') {
                delete newConfig[currentLanguage][key];
                if (!Object.keys(newConfig[currentLanguage]).length) delete newConfig[currentLanguage];
            } else newConfig[currentLanguage][key] = newValue;
            setConfig({ ...newConfig });
        },
        [currentLanguage, setConfig, config]
    );

    const languagesOptions = React.useMemo(
        () =>
            ['default'].concat(languages).map(lang => {
                return {
                    label: LANGUAGES[lang] || lang,
                    value: lang
                };
            }),
        [languages]
    );

    if (!Object.keys(fields).length) {
        return null;
    }

    return (
        <FrameCard
            title={
                <div className={Style['customization-title']}>
                    {t('ticket.ticket_design.create_design.customization')}
                    {languagesOptions?.length > 0 && (
                        <TranslateDropdown onChange={selected => setCurrentLanguage(selected)} defaultValue={'default'} selected={selectedLanguages} languages={languagesOptions} />
                    )}
                </div>
            }
        >
            <div>
                {fields &&
                    Object.entries(fields)?.map(([key, field]) => {
                        let value = '';
                        if (Object.keys(config).includes(currentLanguage) && Object.keys(config[currentLanguage]).includes(key)) value = config[currentLanguage][key];
                        else if (Object.keys(config).includes('default') && Object.keys(config['default']).includes(key)) value = config['default'][key];

                        return (
                            <FormComponent
                                key={key}
                                type={field.type}
                                label={labels[key]}
                                description={descriptions[key]}
                                labelOptions={labels[`${key}_options`]}
                                onChange={newValue => {
                                    updateConfig(key, newValue);
                                }}
                                defaultValue={value}
                                options={field.options}
                                params={field.params || {}}
                            />
                        );
                    })}
            </div>
        </FrameCard>
    );
}
